import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AffiliateDashboard() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const key = searchParams.get('key');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLogs, setShowLogs] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const res = await fetch(`${API_URL}/api/affiliate/${slug}/stats?key=${key}`);
        if (!res.ok) {
          const err = await res.text();
          throw new Error(res.status === 403 ? 'Invalid dashboard key' : 'Partner not found');
        }
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    if (slug && key) fetchStats();
    else { setError('Missing dashboard key'); setLoading(false); }
  }, [slug, key]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="bg-slate-800 rounded-xl p-8 border border-slate-700 text-center max-w-md">
          <p className="text-red-400 text-lg">{error}</p>
          <p className="text-slate-400 mt-2 text-sm">Check that your dashboard link is correct.</p>
        </div>
      </div>
    );
  }

  const conversionRate = data.clicks > 0 ? ((data.signups / data.clicks) * 100).toFixed(1) : '0';
  const paidRate = data.signups > 0 ? ((data.conversions / data.signups) * 100).toFixed(1) : '0';
  const totalCommission = data.total_commission_earned ?? data.commission_earned ?? 0;

  return (
    <div className="min-h-screen bg-slate-900">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-white">Affiliate Dashboard</h1>
          <p className="text-slate-400 mt-1">Welcome, {data.name}</p>
          <div className="flex items-center gap-3 mt-3">
            <span className={`px-2 py-1 rounded text-xs font-medium ${data.active ? 'bg-green-600/20 text-green-400 border border-green-700' : 'bg-red-600/20 text-red-400 border border-red-700'}`}>
              {data.active ? 'Active' : 'Inactive'}
            </span>
            {data.discount_code && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-purple-600/20 text-purple-400 border border-purple-700">
                Discount: {data.discount_code}
              </span>
            )}
          </div>
        </div>

        {/* Your Link */}
        <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-8">
          <p className="text-slate-400 text-sm mb-2">Your referral link</p>
          <div className="flex items-center gap-3">
            <code className="bg-slate-900 px-4 py-2 rounded-lg text-blue-400 text-sm flex-1 overflow-x-auto">
              https://tracjobsalert.co.uk/r/{data.slug}
            </code>
            <button
              onClick={() => { navigator.clipboard.writeText(`https://tracjobsalert.co.uk/r/${data.slug}`); }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
            >
              Copy
            </button>
          </div>
        </div>

        {/* Funnel Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Link Clicks</p>
            <p className="text-3xl font-bold text-white mt-2">{data.clicks}</p>
          </div>
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Signups</p>
            <p className="text-3xl font-bold text-blue-400 mt-2">{data.signups}</p>
            <p className="text-slate-500 text-xs mt-1">{conversionRate}% of clicks</p>
          </div>
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Paid Subscribers</p>
            <p className="text-3xl font-bold text-green-400 mt-2">{data.conversions}</p>
            <p className="text-slate-500 text-xs mt-1">{paidRate}% of signups</p>
          </div>
          <div className="bg-slate-800 rounded-xl p-5 border border-slate-700">
            <p className="text-slate-400 text-xs uppercase tracking-wider">Total Commission</p>
            <p className="text-3xl font-bold text-amber-400 mt-2">£{totalCommission.toFixed(2)}</p>
            <p className="text-slate-500 text-xs mt-1">{data.commission_rate}% per payment</p>
          </div>
        </div>

        {/* Commission Logs Toggle */}
        {data.commission_logs && data.commission_logs.length > 0 && (
          <div className="mb-8">
            <button
              onClick={() => setShowLogs(!showLogs)}
              className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
            >
              <svg className={`w-4 h-4 transition-transform ${showLogs ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
              {showLogs ? 'Hide' : 'Show'} Commission History ({data.commission_logs.length} entries)
            </button>
            
            {showLogs && (
              <div className="mt-4 bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <div className="px-6 py-4 border-b border-slate-700">
                  <h2 className="text-white font-semibold">Commission History</h2>
                  <p className="text-slate-500 text-xs mt-1">Tracks every payment, refund, and adjustment</p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">Date</th>
                        <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">Type</th>
                        <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">User</th>
                        <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">Description</th>
                        <th className="text-right px-6 py-3 text-slate-400 text-xs uppercase">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.commission_logs.map((log, i) => (
                        <tr key={log.id || i} className="border-b border-slate-700/50">
                          <td className="px-6 py-3 text-slate-400 text-sm">{log.date || '-'}</td>
                          <td className="px-6 py-3">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              log.event_type === 'payment' 
                                ? 'bg-green-600/20 text-green-400' 
                                : log.event_type === 'refund'
                                ? 'bg-red-600/20 text-red-400'
                                : 'bg-slate-600/20 text-slate-400'
                            }`}>
                              {log.event_type}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-white text-sm">{log.user_email}</td>
                          <td className="px-6 py-3 text-slate-400 text-sm">{log.description}</td>
                          <td className={`px-6 py-3 text-right font-medium ${log.amount >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {log.amount >= 0 ? '+' : ''}£{log.amount.toFixed(2)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Converted Users */}
        {data.converted_users && data.converted_users.length > 0 && (
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700">
              <h2 className="text-white font-semibold">Your Subscribers</h2>
              <p className="text-slate-500 text-xs mt-1">Users who signed up through your link and paid</p>
            </div>
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">#</th>
                  <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">Email</th>
                  <th className="text-left px-6 py-3 text-slate-400 text-xs uppercase">Signup Date</th>
                </tr>
              </thead>
              <tbody>
                {data.converted_users.map((user, i) => (
                  <tr key={i} className="border-b border-slate-700/50">
                    <td className="px-6 py-3 text-slate-400">{i + 1}</td>
                    <td className="px-6 py-3 text-white">{user.email}</td>
                    <td className="px-6 py-3 text-slate-400">{user.date || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data.converted_users && data.converted_users.length === 0 && (
          <div className="bg-slate-800 rounded-xl p-8 border border-slate-700 text-center">
            <p className="text-slate-400">No paid subscribers yet. Share your link to start earning!</p>
          </div>
        )}
      </div>
    </div>
  );
}
